import React, { useState, useEffect, useCallback } from 'react'
import { Card, Input } from 'react-native-elements'
import PropTypes from 'prop-types'
import {
  View,
  TouchableOpacity,
  ScrollView,
  SafeAreaView
} from 'react-native'
import Icon from '../Icons'
import Modal from '../Modal'
import ListWithInfiniteScroll from '../ListWithInfiniteScroll'
import { useTheme } from '../../theming/ThemeProvider'
import { useStyles } from './useStyles'
import Option from './Option'
import MultiselectedOptions from './MultiselectedOption'
import Spinner from '../Spinner'
import { useDimensions } from '../../hooks/useDimensions'

export default function SelectInputOptionsModal (props) {
  const {
    open, selected, placeholder, options, keyProp, nameProp,
    getOptionLabel, onClose, multiselect, loading, filterAsync,
    pagination, noMoreOptions, loadMore, globalLoading
  } = props
  const [inputValue, setInputValue] = useState('')
  const [selectedOptions, setSelectedOptions] = useState(selected)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const { theme } = useTheme()
  const { height } = useDimensions()
  const styles = useStyles(theme, height)

  const updateSelectOptions = useCallback(() => {
    setSelectedOptions(selected)
    setInputValue(selected ? (getOptionLabel ? getOptionLabel(selected) : selected[nameProp]) : '')
  }, [getOptionLabel, nameProp, selected])

  const updateMultiselectOptions = useCallback(() => {
    setSelectedOptions(selected && selected.length ? selected : [])
  }, [selected])

  useEffect(() => {
    if (open) {
      if (multiselect) {
        updateMultiselectOptions()
      } else {
        updateSelectOptions()
      }
    }
  }, [open, multiselect, updateMultiselectOptions, updateSelectOptions])

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  const onInputChange = (newValue) => {
    setInputValue(newValue)
    filterOptions(newValue)
  }

  const onSelectOption = (option) => {
    if (multiselect) {
      const alreadyChecked = isOptionSelected(option)
      if (alreadyChecked) {
        deselectOption(option)
      } else {
        setSelectedOptions([
          ...selectedOptions,
          option
        ])
      }
    } else {
      onCloseModal(option)
    }
  }

  const onCloseModal = (item) => {
    onClose(item || selectedOptions)
  }

  const filterOptions = (value) => {
    if (filterAsync && open) {
      filterAsync(value.toLowerCase())
    } else {
      setFilteredOptions(
        options.filter(option =>
          option[nameProp].toLowerCase().includes(value ? value.toLowerCase() : '')
        )
      )
    }
  }

  const deselectOption = (option) => {
    setSelectedOptions(selectedOptions.filter(item => item[keyProp] !== option[keyProp]))
  }

  const isOptionSelected = (option) => Boolean(
    selectedOptions && selectedOptions.filter(item => item[keyProp] === option[keyProp]).length
  )

  return (
    <Modal
      open={open}
      onClose={() => {}}
      hideArrow
    >
      <View style={styles.container} testID='modalSelectOption' accessibilityLabel='modalSelectOption'>
        <SafeAreaView />
        <View horizontal style={styles.inputContainer}>
          <TouchableOpacity activeOpacity={0.8} onPress={() => onCloseModal()}>
            <Icon
              name='LeftArrow'
              height={20}
              width={20}
              style={styles.inputIcon}
              fill={theme.ICON_COLOR}
            />
          </TouchableOpacity>
          <ScrollView
            horizontal
            contentContainerStyle={styles.inputScrollViewContainer}
            showsHorizontalScrollIndicator={false}
          >
            {multiselect
              ? (
                <MultiselectedOptions {...{
                  getOptionLabel,
                  deselectOption,
                  selectedOptions,
                  nameProp,
                  styles
                }}
                />)
              : null}
            <Input
              value={inputValue}
              placeholder={placeholder}
              inputContainerStyle={styles.elementsInputContainer}
              inputStyle={styles.elementsInput}
              onChangeText={onInputChange}
              placeholderTextColor={theme.INPUT_COLOR}
            />
          </ScrollView>
        </View>
        <Card containerStyle={styles.optionsContainer}>
          {globalLoading ? (
            <Spinner />
          ) : (
            <>
              <ListWithInfiniteScroll
                data={filterAsync ? options : filteredOptions}
                component={Option}
                noMore={pagination ? noMoreOptions : true}
                handleLoadMore={loadMore}
                loading={loading}
                cardProps={{
                  multiselect,
                  selected,
                  nameProp,
                  onSelectOption,
                  getOptionLabel,
                  isOptionSelected,
                  theme
                }}
                noDataMessage='No results found'
              />
              <SafeAreaView />
            </>
          )}
        </Card>
      </View>
    </Modal>
  )
}

SelectInputOptionsModal.propTypes = {
  open: PropTypes.bool,
  selected: PropTypes.any,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  keyProp: PropTypes.string,
  nameProp: PropTypes.string,
  onClose: PropTypes.func,
  multiselect: PropTypes.bool,
  loading: PropTypes.bool,
  filterAsync: PropTypes.func,
  getOptionLabel: PropTypes.func,
  pagination: PropTypes.bool,
  noMoreOptions: PropTypes.bool,
  loadMore: PropTypes.func,
  globalLoading: PropTypes.bool
}

SelectInputOptionsModal.defaultProps = {
  open: false,
  selected: null,
  placeholder: '',
  options: [],
  keyProp: '',
  nameProp: '',
  onClose: () => {},
  multiselect: false,
  loading: false,
  filterAsync: null,
  getOptionLabel: null,
  pagination: false,
  noMoreOptions: false,
  loadMore: null,
  globalLoading: false
}
